<template>
  <div>
    <!-- Status incidents banner -->
    <div
      v-if="shouldShowStatusBanner"
      @click="openStatusPage"
      class="relative"
    >
      <!-- Dismiss button -->
      <button
        @click.stop="dismissBanner"
        class="absolute top-1/2 -translate-y-1/2 right-4 z-10 text-white hover:text-gray-200 transition-colors"
        aria-label="Dismiss banner"
      >
        <o-icon icon="x" :size="16" />
      </button>

      <!-- Show ongoing incidents -->
      <div v-if="hasOngoingIncidents" class="space-y-2 bg-red-600 py-3 px-5 pr-10">
        <a 
          v-for="incident in statusData.ongoing_incidents" 
          :key="incident.id"
          class="flex items-center space-x-2"
          :href="incident.url"
          target="_blank group block"
        >
          <o-icon icon="warning" class="text-white mr-5 inline-block" :size="18" />
          <div>
            <div class="flex items-center space-x-2">
              <p class="text-white font-semibold leading-none text-sm group-hover:underline underline-white">
                We're currently experiencing issues - {{ incident.name }}
              </p>
              <span class="text-white text-opacity-75 text-sm">
                {{ formatDate(incident.last_update_at) }}
              </span>
            </div>
            <p class="text-white text-opacity-90 text-sm leading-tight">
              {{ incident.last_update_message }}
            </p>
          </div>
        </a>
      </div>

      <!-- Show in-progress maintenance if no ongoing incidents -->
      <div v-else-if="hasInProgressMaintenance" class="bg-orange-500 py-3 px-5 pr-10">
        <div 
          v-for="maintenance in statusData.in_progress_maintenances.slice(0, 2)" 
          :key="maintenance.id"
        >
          <p class="text-white text-opacity-90 text-sm leading-tight">
            We're currently undergoing maintenance - {{ maintenance.name }}
          </p>
        </div>
      </div>

      <!-- Show scheduled maintenance if no other issues -->
      <div v-else-if="hasScheduledMaintenance" class="bg-blue-600 py-3 px-5 pr-10">
        <div 
          class="border-l-2 border-white border-opacity-30 pl-3"
        >
          <p class="text-white text-opacity-90 text-sm leading-tight">
            Scheduled Maintenance - {{ maintenance.name }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data () {
    return {
      statusData: null,
      statusError: false,
      dismissed: false
    }
  },
  computed: {
    hasOngoingIncidents () {
      return this.statusData?.ongoing_incidents?.length > 0
    },
    hasInProgressMaintenance () {
      return this.statusData?.in_progress_maintenances?.length > 0
    },
    hasScheduledMaintenance () {
      return this.statusData?.scheduled_maintenances?.length > 0
    },
    shouldShowStatusBanner () {
      return !this.dismissed && (this.hasOngoingIncidents || this.hasInProgressMaintenance || this.hasScheduledMaintenance)
    },
  },
  async mounted () {
    await this.fetchStatusData()
    // Refresh status every 5 minutes
    this.statusInterval = setInterval(() => {
      this.fetchStatusData()
    }, 5 * 60 * 1000)
  },
  beforeDestroy () {
    if (this.statusInterval) {
      clearInterval(this.statusInterval)
    }
  },
  methods: {
    async fetchStatusData () {
      try {
        const response = await axios.get('https://status.ovatu.com/api/v1/summary')
        this.statusData = response.data
        this.statusError = false
      } catch (error) {
        console.warn('Failed to fetch status data:', error)
        this.statusError = true
      }
    },
    formatDate (dateString) {
      try {
        const date = new Date(dateString)
        const now = new Date()
        const diffInMinutes = Math.floor((now - date) / (1000 * 60))
        
        if (diffInMinutes < 1) {
          return 'Just now'
        } else if (diffInMinutes < 60) {
          return `${diffInMinutes}m ago`
        } else if (diffInMinutes < 1440) {
          const hours = Math.floor(diffInMinutes / 60)
          return `${hours}h ago`
        } else {
          return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          })
        }
      } catch (error) {
        return ''
      }
    },
    openStatusPage () {
      if (this.statusData?.page_url) {
        window.open(this.statusData.page_url, '_blank')
      }
    },
    dismissBanner () {
      this.dismissed = true
    }
  }
}
</script>
